<template>
  <main class="landing-page">
    <nav class="landing-nav">
      <router-link class="brand" to="/">
        <span class="brand-mark"><img src="/img/boarua-icon-concept-v1.png" alt="" /></span>
        <strong>BOARUA</strong>
      </router-link>

      <div class="nav-actions">
        <a href="#recursos" @click.prevent="scrollToSection('recursos')">Recursos</a>
        <a href="#como-funciona" @click.prevent="scrollToSection('como-funciona')">Como funciona</a>
        <a href="#para-quem" @click.prevent="scrollToSection('para-quem')">Para quem</a>
        <a href="#planos" @click.prevent="scrollToSection('planos')">Planos</a>
        <router-link class="nav-link" to="/login">Entrar</router-link>
        <router-link class="nav-button" to="/criar-conta">Criar conta</router-link>
      </div>
    </nav>

    <section class="hero-section">
      <div class="hero-copy">
        <h1>A vida do seu local organizada em um só lugar</h1>
        <p>
          Chamados, avisos, cobranças, assembleias, visitantes e segurança com mais clareza para moradores,
          administradores e equipes do dia a dia.
        </p>

        <div class="hero-audience" aria-label="Quem pode contratar o BOARUA">
          <span>Feito para</span>
          <div>
            <strong>
              <span class="condo-icons" aria-hidden="true">
                <v-icon size="18">mdi-office-building-outline</v-icon>
                <v-icon size="18">mdi-home-outline</v-icon>
              </span>
              Condomínios
            </strong>
            <strong><v-icon size="18">mdi-home-group</v-icon>Associações</strong>
            <strong><v-icon size="18">mdi-domain</v-icon>Imobiliárias</strong>
          </div>
        </div>

        <div class="hero-actions">
          <router-link class="primary-action" to="/criar-conta">
            Criar conta
            <v-icon color="white" small>mdi-arrow-right</v-icon>
          </router-link>
          <router-link class="secondary-action" to="/login">Entrar</router-link>
        </div>
      </div>

      <div class="hero-image" aria-label="Família chegando em condomínio com portaria">
        <img src="/img/boarua-hero-essence-v1.jpg" alt="" />
      </div>
    </section>

    <section class="intro-strip" aria-label="Resumo dos benefícios">
      <article v-for="item in introStats" :key="item.title">
        <strong>{{ item.title }}</strong>
        <span>{{ item.text }}</span>
      </article>
    </section>

    <section id="recursos" class="section-block resources-section">
      <div class="section-heading centered">
        <h2>O essencial do local, sem complicação.</h2>
        <p>Organize avisos, cobranças, chamados, assembleias, portaria e segurança em um só lugar.</p>
      </div>

      <div class="resource-grid">
        <article v-for="feature in features" :key="feature.title">
          <v-icon size="30">{{ feature.icon }}</v-icon>
          <h3>{{ feature.title }}</h3>
          <p>{{ feature.text }}</p>
        </article>
      </div>
    </section>

    <section id="como-funciona" class="steps-section">
      <div class="section-heading centered">
        <h2>Tudo começa em poucos passos.</h2>
      </div>

      <div class="steps-list">
        <article v-for="(step, index) in steps" :key="step.title">
          <span class="step-icon"><v-icon size="38">{{ step.icon }}</v-icon></span>
          <small>{{ index + 1 }}. {{ step.title }}</small>
          <p>{{ step.text }}</p>
        </article>
      </div>
    </section>

    <section class="preview-section">
      <div class="preview-copy">
        <h2>Uma rotina mais simples para quem administra e para quem mora.</h2>
        <p>
          Cada pessoa encontra o que precisa sem procurar em várias mensagens: avisos, boletos, pedidos,
          documentos, assembleias, visitantes e ocorrências.
        </p>

        <ul class="check-list">
          <li v-for="item in dailyItems" :key="item">
            <v-icon size="18">mdi-check</v-icon>
            <span>{{ item }}</span>
          </li>
        </ul>
      </div>

      <div class="product-preview" aria-label="Prévia ilustrativa do BOARUA">
        <div class="phone-preview">
          <div class="phone-top">BOARUA</div>
          <div class="hello-card">
            <strong>Olá, Ana!</strong>
            <span>Bem-vinda ao seu local.</span>
          </div>
          <div v-for="item in phoneItems" :key="item.title" class="phone-row">
            <v-icon size="18">{{ item.icon }}</v-icon>
            <div>
              <strong>{{ item.title }}</strong>
              <span>{{ item.text }}</span>
            </div>
          </div>
        </div>

        <div class="desktop-preview">
          <div class="desktop-top">
            <strong>BOARUA</strong>
            <span>Hoje</span>
          </div>
          <div class="desktop-body">
            <aside>
              <span v-for="item in previewMenu" :key="item" :class="{ active: item === 'Início' }">{{ item }}</span>
            </aside>
            <section>
              <h3>Olá, Ana!</h3>
              <p>Veja os avisos recentes e acompanhe o que está acontecendo.</p>
              <div class="notice-list">
                <article v-for="item in noticeItems" :key="item.title">
                  <v-icon size="20">{{ item.icon }}</v-icon>
                  <div>
                    <strong>{{ item.title }}</strong>
                    <span>{{ item.text }}</span>
                  </div>
                </article>
              </div>
              <div class="summary-grid">
                <span>Meu boleto<strong>R$ 350,00</strong></span>
                <span>Meus chamados<strong>1 em andamento</strong></span>
                <span>Ocorrências<strong>Nenhuma nova</strong></span>
              </div>
            </section>
          </div>
        </div>
      </div>
    </section>

    <section id="para-quem" class="audience-section">
      <div class="section-heading centered">
        <h2>Benefícios para todos que fazem parte do local.</h2>
      </div>

      <div class="audience-grid">
        <article v-for="audience in audiences" :key="audience.title">
          <v-icon size="36">{{ audience.icon }}</v-icon>
          <h3>{{ audience.title }}</h3>
          <ul>
            <li v-for="benefit in audience.benefits" :key="benefit">
              <v-icon size="16">mdi-check</v-icon>
              <span>{{ benefit }}</span>
            </li>
          </ul>
        </article>
      </div>
    </section>

    <section id="planos" class="pricing-section">
      <div class="section-heading centered">
        <h2>Planos que cabem no seu local.</h2>
        <p>
          Todos os planos incluem os mesmos recursos. O que muda é o tamanho do local:
          residências, apartamentos e comércios atendidos.
        </p>
      </div>

      <div class="landing-plans-grid">
        <article
          v-for="plan in pricingPlans"
          :key="plan.name"
          :class="['landing-plan-card', { recommended: plan.recommended }]"
        >
          <span v-if="plan.recommended" class="plan-ribbon">Mais escolhido</span>
          <h3>{{ plan.name }}</h3>
          <p>{{ plan.description }}</p>

          <div class="landing-plan-price">
            <template v-if="plan.price">
              <small>R$</small>
              <strong>{{ plan.price }}</strong>
              <em>/mês</em>
            </template>
            <strong v-else class="custom-price">Sob consulta</strong>
          </div>

          <span class="plan-limit">{{ plan.limit }}</span>

          <div class="plan-capacity">
            <div>
              <span>Residências e apartamentos</span>
              <strong>{{ plan.residences }}</strong>
            </div>
            <div>
              <span>Comércios</span>
              <strong>{{ plan.commerces }}</strong>
            </div>
          </div>

          <router-link class="plan-action" to="/criar-conta">Escolher plano</router-link>
        </article>
      </div>

      <p class="pricing-note">
        Todos os planos incluem suporte humano. Caso o local cresça, é possível mudar para uma faixa maior.
        Taxas de boleto, Pix ou cartão podem variar conforme o meio de pagamento escolhido.
      </p>
    </section>

    <section class="trust-section">
      <div class="section-heading centered">
        <h2>Transparência que gera confiança.</h2>
      </div>

      <div class="trust-grid">
        <article v-for="item in trustItems" :key="item.title">
          <v-icon size="34">{{ item.icon }}</v-icon>
          <h3>{{ item.title }}</h3>
          <p>{{ item.text }}</p>
        </article>
      </div>
    </section>

    <section class="help-section">
      <div>
        <h2>Dúvidas comuns antes de começar.</h2>
        <p>Respostas simples para quem quer organizar o local com calma e segurança.</p>
      </div>

      <div class="help-list">
        <article v-for="item in faqItems" :key="item.question">
          <strong>{{ item.question }}</strong>
          <p>{{ item.answer }}</p>
        </article>
      </div>
    </section>

    <section class="final-cta">
      <h2>Organize seu local hoje mesmo.</h2>
      <p>Menos confusão, mais colaboração e uma comunidade melhor para todos.</p>
      <div class="hero-actions">
        <router-link class="primary-action light" to="/criar-conta">Criar conta</router-link>
        <router-link class="secondary-action dark" to="/login">Entrar</router-link>
      </div>
    </section>
  </main>
</template>

<script>
export default {
  name: "Landing",
  data: () => ({
    introStats: [
      { title: "Avisos no lugar certo", text: "Moradores recebem comunicados sem depender de mensagens perdidas." },
      { title: "Cobranças mais claras", text: "Cada pessoa acompanha valores, vencimentos e pagamentos." },
      { title: "Mais controle no dia a dia", text: "Chamados, visitantes e ocorrências ficam registrados para consulta." },
    ],
    features: [
      {
        icon: "mdi-bullhorn-outline",
        title: "Avisos e comunicados",
        text: "Informe moradores sem depender de mensagens perdidas.",
      },
      {
        icon: "mdi-clipboard-text-outline",
        title: "Chamados e solicitações",
        text: "Receba pedidos e acompanhe o andamento.",
      },
      {
        icon: "mdi-credit-card-outline",
        title: "Cobranças e pagamentos",
        text: "Mostre valores, vencimentos e pagamentos com clareza.",
      },
      {
        icon: "mdi-account-voice",
        title: "Assembleias e votações",
        text: "Facilite presenças, convites e decisões da comunidade.",
      },
      {
        icon: "mdi-gate",
        title: "Visitantes e portaria",
        text: "Controle acessos e mantenha o histórico organizado.",
      },
      {
        icon: "mdi-shield-alert-outline",
        title: "Segurança e ocorrências",
        text: "Registre alertas e situações importantes do local.",
      },
    ],
    steps: [
      {
        icon: "mdi-home-plus-outline",
        title: "Crie seu local",
        text: "Cadastre rua, bairro, condomínio, associação ou negócio em poucos minutos.",
      },
      {
        icon: "mdi-account-multiple-plus-outline",
        title: "Convide as pessoas",
        text: "Inclua moradores, responsáveis, portaria, segurança e comércios.",
      },
      {
        icon: "mdi-bullhorn-outline",
        title: "Organize e comunique",
        text: "Envie avisos, publique informações e abra chamados de forma simples.",
      },
      {
        icon: "mdi-shield-check-outline",
        title: "Tenha mais clareza",
        text: "Acompanhe tudo em um só lugar e tome decisões com mais segurança.",
      },
    ],
    dailyItems: [
      "Avisos e comunicados",
      "Chamados e solicitações",
      "Cobranças e pagamentos",
      "Assembleias e votações",
      "Documentos e registros",
      "Segurança e ocorrências",
    ],
    phoneItems: [
      { icon: "mdi-bullhorn-outline", title: "Aviso recente", text: "Manutenção no playground" },
      { icon: "mdi-calendar-check-outline", title: "Assembleia", text: "Quinta-feira, 19h" },
      { icon: "mdi-credit-card-outline", title: "Boleto", text: "Vencimento em 5 dias" },
    ],
    previewMenu: ["Início", "Avisos", "Chamados", "Cobranças", "Assembleias", "Documentos"],
    noticeItems: [
      { icon: "mdi-home-outline", title: "Manutenção no playground", text: "Sábado, das 9h às 12h." },
      { icon: "mdi-account-voice", title: "Assembleia geral", text: "Dia 20, às 19h." },
      { icon: "mdi-shield-alert-outline", title: "Coleta de lixo especial", text: "Dia 25, a partir das 7h." },
    ],
    audiences: [
      {
        icon: "mdi-home-outline",
        title: "Moradores",
        benefits: ["Avisos e documentos", "Cobranças em dia", "Participação nas decisões"],
      },
      {
        icon: "mdi-account-tie-outline",
        title: "Administradores",
        benefits: ["Comunicação centralizada", "Menos retrabalho", "Prestação de contas clara"],
      },
      {
        icon: "mdi-shield-check-outline",
        title: "Portaria e segurança",
        benefits: ["Registro de acessos", "Ocorrências organizadas", "Alertas importantes"],
      },
      {
        icon: "mdi-storefront-outline",
        title: "Comércio local",
        benefits: ["Ofertas e serviços", "Mais proximidade", "Mais visibilidade"],
      },
    ],
    pricingPlans: [
      {
        name: "Basic",
        description: "Para locais menores que querem organizar a rotina com simplicidade.",
        price: "129",
        limit: "até 50 unidades",
        residences: "50",
        commerces: "1",
      },
      {
        name: "Plus",
        description: "Para comunidades em crescimento, com mais moradores e comércios.",
        price: "199",
        limit: "até 150 unidades",
        residences: "150",
        commerces: "3",
        recommended: true,
      },
      {
        name: "Pro",
        description: "Para condomínios, bairros e associações com maior volume de pessoas.",
        price: "299",
        limit: "até 400 unidades",
        residences: "400",
        commerces: "6",
      },
      {
        name: "Premium",
        description: "Para locais grandes que precisam atender muitas unidades no mesmo ambiente.",
        price: "499",
        limit: "até 1.000 unidades",
        residences: "1.000",
        commerces: "10",
      },
      {
        name: "Enterprise",
        description: "Para locais acima das faixas padrão, com condição combinada caso a caso.",
        price: null,
        limit: "acima de 1.000 unidades",
        residences: "sob consulta",
        commerces: "sob consulta",
      },
    ],
    trustItems: [
      {
        icon: "mdi-file-document-check-outline",
        title: "Prestação de contas clara",
        text: "Relatórios e históricos ficam disponíveis para consulta.",
      },
      {
        icon: "mdi-lock-check-outline",
        title: "Dados protegidos",
        text: "As informações do local são cuidadas com atenção e segurança.",
      },
      {
        icon: "mdi-account-group-outline",
        title: "Participação de verdade",
        text: "Moradores acompanham decisões e entendem o que está acontecendo.",
      },
      {
        icon: "mdi-currency-usd",
        title: "Sem taxa escondida",
        text: "Você sabe exatamente pelo que está pagando.",
      },
    ],
    faqItems: [
      {
        question: "É fácil de usar?",
        answer: "Sim. A tela foi pensada para tarefas comuns: enviar aviso, acompanhar pagamento, abrir chamado e consultar informações.",
      },
      {
        question: "Serve para rua, bairro ou condomínio?",
        answer: "Sim. O BOARUA pode ser usado por associações, ruas, condomínios, loteamentos, comércios e comunidades organizadas.",
      },
      {
        question: "Os moradores também acessam?",
        answer: "Sim. Cada morador pode acompanhar avisos, cobranças, documentos, assembleias, chamados e autorizações.",
      },
    ],
  }),
  methods: {
    scrollToSection(id) {
      const target = document.getElementById(id);

      if (target) {
        const navOffset = 76;
        const top = target.getBoundingClientRect().top + window.pageYOffset - navOffset;
        const scroller = document.scrollingElement || document.documentElement;

        if (scroller && typeof scroller.scrollTo === "function") {
          scroller.scrollTo({ top, behavior: "smooth" });
        } else {
          window.scrollTo({ top, behavior: "smooth" });
        }
      }
    },
  },
};
</script>

<style scoped lang="scss">
.landing-page {
  min-height: 100vh;
  color: #07140f;
  background: #ffffff;
}

h1,
h2,
h3,
p {
  margin: 0;
}

.landing-nav {
  position: sticky;
  z-index: 20;
  top: 0;
  min-height: 72px;
  padding: 12px clamp(18px, 5vw, 72px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(255, 255, 255, 0.94);
  border-bottom: 1px solid #e8eee9;
  backdrop-filter: blur(14px);
}

.brand,
.nav-actions,
.nav-button,
.nav-link,
.primary-action,
.secondary-action {
  display: inline-flex;
  align-items: center;
}

.brand {
  gap: 11px;
  color: #003f2d;
  text-decoration: none;

  strong {
    font-size: 25px;
    font-weight: 900;
    letter-spacing: 0;
  }
}

.brand-mark {
  width: 38px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #ffffff;
  border: 1px solid #e0e9e2;
  border-radius: 8px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.nav-actions {
  gap: 24px;

  a {
    color: #14231d;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.02em;
    text-decoration: none;
    text-transform: uppercase;
    cursor: pointer;
  }
}

.nav-button {
  min-height: 42px;
  padding: 0 18px;
  color: #ffffff !important;
  background: #003f2d;
  border-radius: 6px;
}

.hero-section {
  min-height: min(760px, calc(100vh - 150px));
  display: flex;
  align-items: stretch;
  background-image:
    linear-gradient(90deg, #ffffff 0%, rgba(255, 255, 255, 0.96) 35%, rgba(255, 255, 255, 0.7) 51%, rgba(255, 255, 255, 0) 68%),
    url("/img/boarua-hero-essence-v1.jpg");
  background-position: center right;
  background-size: cover;
  background-repeat: no-repeat;
  border-bottom: 1px solid #e8eee9;
  overflow: hidden;
}

.hero-copy {
  width: min(760px, 58vw);
  padding: clamp(46px, 7vw, 82px) clamp(22px, 5vw, 72px);
  display: flex;
  flex-direction: column;
  justify-content: center;

  h1 {
    max-width: 720px;
    font-size: clamp(42px, 5.6vw, 74px);
    font-weight: 900;
    line-height: 1.03;
  }

  p {
    max-width: 590px;
    margin-top: 24px;
    color: #4f5e57;
    font-size: clamp(18px, 2vw, 23px);
    line-height: 1.5;
  }
}

.hero-image {
  display: none;

  img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
  }
}

.hero-audience {
  max-width: 620px;
  margin-top: 24px;

  > span {
    display: block;
    margin-bottom: 8px;
    color: #536760;
    font-size: 14px;
    font-weight: 900;
    line-height: 1;
    text-transform: uppercase;
    letter-spacing: 0;
  }

  > div {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
  }

  strong {
    min-height: 38px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    padding: 0 10px;
    color: #004630;
    background: rgba(255, 255, 255, 0.86);
    border: 1px solid rgba(0, 70, 48, 0.18);
    border-radius: 999px;
    box-shadow: 0 10px 28px rgba(0, 70, 48, 0.08);
    font-size: 12px;
    font-weight: 900;
    line-height: 1.1;
    text-align: center;
    white-space: nowrap;
  }

  .v-icon {
    flex: 0 0 auto;
    width: 18px;
    height: 18px;
    color: #006056;
  }

  .condo-icons {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 3px;

    .v-icon {
      width: 18px;
      height: 18px;
    }
  }
}

.hero-actions {
  margin-top: 32px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.primary-action,
.secondary-action {
  min-height: 48px;
  min-width: 146px;
  justify-content: center;
  padding: 0 20px;
  gap: 9px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 900;
  text-decoration: none;
}

.primary-action {
  color: #ffffff;
  background: #004630;
  box-shadow: 0 16px 34px rgba(0, 70, 48, 0.16);

  &.light {
    color: #004630;
    background: #ffffff;
  }
}

.secondary-action {
  color: #004630;
  background: #ffffff;
  border: 1px solid #004630;

  &.dark {
    color: #ffffff;
    background: transparent;
    border-color: rgba(255, 255, 255, 0.8);
  }
}

.intro-strip {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  background: #f6faf7;
  border-bottom: 1px solid #e4ede6;

  article {
    min-height: 132px;
    padding: 30px clamp(18px, 4vw, 54px);
    border-right: 1px solid #e4ede6;
  }

  strong,
  span {
    display: block;
  }

  strong {
    color: #003f2d;
    font-size: 20px;
    font-weight: 900;
  }

  span {
    max-width: 330px;
    margin-top: 9px;
    color: #5a675f;
    font-size: 15px;
    line-height: 1.45;
  }
}

.section-block,
.steps-section,
.preview-section,
.audience-section,
.pricing-section,
.trust-section,
.help-section,
.final-cta {
  padding: clamp(64px, 8vw, 104px) clamp(18px, 5vw, 72px);
}

.resources-section {
  padding-top: clamp(44px, 6vw, 72px);
  padding-bottom: clamp(44px, 6vw, 72px);
}

.audience-section {
  padding-top: clamp(48px, 6vw, 76px);
  padding-bottom: clamp(48px, 6vw, 76px);
}

.section-heading {
  max-width: 780px;
  margin-bottom: 38px;

  &.centered {
    margin-right: auto;
    margin-left: auto;
    text-align: center;
  }

  h2 {
    color: #07140f;
    font-size: clamp(32px, 4.4vw, 56px);
    font-weight: 900;
    line-height: 1.08;
  }

  p {
    margin-top: 16px;
    color: #596960;
    font-size: 17px;
    line-height: 1.55;
  }
}

.resource-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  border: 1px solid #e2ebe4;
  border-radius: 8px;
  overflow: hidden;

  article {
    min-height: 168px;
    padding: 22px 24px;
    background: #ffffff;
    border-right: 1px solid #e2ebe4;
    border-bottom: 1px solid #e2ebe4;
  }

  .v-icon {
    color: #004630;
  }

  h3 {
    margin-top: 16px;
    color: #07140f;
    font-size: 18px;
    font-weight: 900;
    line-height: 1.15;
  }

  p {
    margin-top: 8px;
    color: #5b6961;
    font-size: 13px;
    line-height: 1.4;
  }
}

.steps-section {
  background: linear-gradient(180deg, #f7fbf8 0%, #ffffff 100%);
  border-top: 1px solid #e7eee8;
  border-bottom: 1px solid #e7eee8;
}

.steps-list {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 22px;

  article {
    position: relative;
    min-height: 210px;
    padding: 12px 16px 0;
    text-align: center;
  }

  article:not(:last-child)::after {
    content: "";
    position: absolute;
    top: 44px;
    right: -30px;
    width: 38px;
    height: 1px;
    background: #b9c9be;
  }

  .step-icon {
    width: 76px;
    height: 76px;
    margin: 0 auto 22px;
    display: grid;
    place-items: center;
    color: #004630;
    border: 2px solid #004630;
    border-radius: 50%;
  }

  small {
    display: block;
    color: #07140f;
    font-size: 15px;
    font-weight: 900;
  }

  p {
    max-width: 240px;
    margin: 12px auto 0;
    color: #596960;
    font-size: 14px;
    line-height: 1.55;
  }
}

.preview-section {
  display: grid;
  grid-template-columns: minmax(280px, 0.42fr) minmax(0, 0.58fr);
  align-items: center;
  gap: clamp(34px, 6vw, 88px);
}

.preview-copy {
  h2 {
    color: #003f2d;
    font-size: clamp(32px, 4vw, 52px);
    font-weight: 900;
    line-height: 1.08;
  }

  p {
    margin-top: 18px;
    color: #4f5e57;
    font-size: 17px;
    line-height: 1.6;
  }
}

.check-list,
.audience-grid ul,
.landing-plan-card ul {
  padding: 0;
  margin: 24px 0 0;
  display: grid;
  gap: 13px;
  list-style: none;

  li {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    color: #33433b;
    font-size: 15px;
    line-height: 1.35;
  }

  .v-icon {
    flex: 0 0 auto;
    color: #004630;
    margin-top: 1px;
  }
}

.product-preview {
  min-height: 520px;
  position: relative;
}

.phone-preview,
.desktop-preview {
  background: #ffffff;
  border: 1px solid #dfe8e2;
  box-shadow: 0 24px 70px rgba(10, 35, 24, 0.12);
}

.phone-preview {
  position: absolute;
  z-index: 2;
  left: 0;
  bottom: 26px;
  width: 190px;
  padding: 13px;
  border-radius: 22px;
}

.phone-top,
.desktop-top {
  color: #004630;
  font-size: 13px;
  font-weight: 900;
}

.hello-card {
  margin-top: 18px;
  padding: 12px;
  background: #f5faf6;
  border-radius: 8px;

  strong,
  span {
    display: block;
  }

  span {
    margin-top: 4px;
    color: #6c7971;
    font-size: 11px;
  }
}

.phone-row,
.notice-list article {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}

.phone-row {
  margin-top: 11px;
  padding: 10px 0;
  border-top: 1px solid #edf2ee;

  .v-icon {
    color: #004630;
  }

  strong,
  span {
    display: block;
  }

  strong {
    color: #07140f;
    font-size: 11px;
  }

  span {
    margin-top: 3px;
    color: #6c7971;
    font-size: 10px;
  }
}

.desktop-preview {
  margin-left: 92px;
  padding: 20px;
  border-radius: 14px;
}

.desktop-top {
  display: flex;
  justify-content: space-between;
  padding-bottom: 16px;
  border-bottom: 1px solid #edf2ee;

  span {
    color: #6a7770;
    font-size: 12px;
  }
}

.desktop-body {
  min-height: 420px;
  display: grid;
  grid-template-columns: 138px 1fr;
  gap: 22px;
  padding-top: 18px;

  aside {
    display: grid;
    align-content: start;
    gap: 8px;

    span {
      padding: 10px;
      color: #65746b;
      border-radius: 6px;
      font-size: 12px;
      font-weight: 800;
    }

    .active {
      color: #ffffff;
      background: #004630;
    }
  }

  section {
    min-width: 0;
  }

  h3 {
    color: #07140f;
    font-size: 25px;
    font-weight: 900;
  }

  p {
    margin-top: 7px;
    color: #637169;
    font-size: 14px;
    line-height: 1.5;
  }
}

.notice-list {
  margin-top: 22px;
  display: grid;
  gap: 10px;

  article {
    padding: 14px;
    background: #f7fbf8;
    border: 1px solid #e6eee8;
    border-radius: 8px;
  }

  .v-icon {
    color: #004630;
  }

  strong,
  span {
    display: block;
  }

  strong {
    color: #07140f;
    font-size: 14px;
  }

  span {
    margin-top: 4px;
    color: #6b7971;
    font-size: 12px;
  }
}

.summary-grid {
  margin-top: 14px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;

  span {
    min-height: 78px;
    padding: 13px;
    color: #6b7971;
    background: #ffffff;
    border: 1px solid #e7eee8;
    border-radius: 8px;
    font-size: 12px;
  }

  strong {
    display: block;
    margin-top: 8px;
    color: #07140f;
    font-size: 15px;
    line-height: 1.2;
  }
}

.audience-section,
.trust-section {
  background: #fbfdfb;
  border-top: 1px solid #e7eee8;
  border-bottom: 1px solid #e7eee8;
}

.audience-grid,
.trust-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1px;
  background: #e1eae4;
  border: 1px solid #e1eae4;
  border-radius: 8px;
  overflow: hidden;

  article {
    min-height: 218px;
    padding: 24px 22px;
    background: #ffffff;
  }

  .v-icon {
    color: #004630;
  }

  h3 {
    margin-top: 16px;
    color: #07140f;
    font-size: 17px;
    font-weight: 900;
    line-height: 1.15;
  }
}

.audience-grid ul {
  margin-top: 13px;

  li {
    gap: 8px;
    font-size: 13px;
    line-height: 1.25;
  }
}

.pricing-section {
  background: #ffffff;
  padding-top: clamp(48px, 6vw, 78px);
  padding-bottom: clamp(48px, 6vw, 78px);
}

.landing-plans-grid {
  max-width: 1280px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  align-items: stretch;
  border: 1px solid #dfe8e2;
  border-radius: 8px;
}

.landing-plan-card {
  position: relative;
  min-height: 390px;
  padding: 30px 22px 22px;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-left: 1px solid #dfe8e2;

  &:first-child {
    border-left: 0;
  }

  &.recommended {
    box-shadow: inset 0 6px 0 #004630;
  }

  h3 {
    color: #07140f;
    font-size: 24px;
    font-weight: 900;
  }

  p {
    min-height: 58px;
    margin-top: 9px;
    color: #64736a;
    font-size: 13px;
    line-height: 1.35;
  }

  ul {
    margin-top: 22px;
  }
}

.plan-ribbon {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  height: 32px;
  display: grid;
  place-items: center;
  color: #ffffff;
  background: #004630;
  font-size: 12px;
  font-weight: 900;
}

.landing-plan-price {
  min-height: 62px;
  margin-top: 14px;
  display: flex;
  align-items: baseline;
  gap: 4px;
  color: #07140f;

  small,
  em {
    color: #5f6e65;
    font-size: 15px;
    font-style: normal;
    font-weight: 900;
  }

  strong {
    font-size: 42px;
    font-weight: 900;
    line-height: 0.95;
  }

  .custom-price {
    font-size: 22px;
  }
}

.plan-limit {
  display: block;
  color: #6a7870;
  font-size: 13px;
  font-weight: 800;
}

.plan-capacity {
  margin-top: 14px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;

  div {
    min-height: 58px;
    padding: 10px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    background: #f7fbf8;
    border: 1px solid #e3ece5;
    border-radius: 8px;
  }

  span,
  strong {
    display: block;
  }

  span {
    color: #64736a;
    font-size: 11px;
    line-height: 1.25;
  }

  strong {
    margin-top: 4px;
    color: #07140f;
    font-size: 17px;
    font-weight: 900;
    line-height: 1.1;
  }
}

.landing-plan-card .plan-includes {
  min-height: auto;
  margin-top: 12px;
  color: #53645b;
  font-size: 12px;
  line-height: 1.45;
}

.plan-action {
  min-height: 40px;
  margin-top: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #004630;
  border: 1px solid #004630;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 900;
  text-decoration: none;
}

.recommended .plan-action {
  color: #ffffff;
  background: #004630;
}

.pricing-note {
  max-width: 760px;
  margin: 22px auto 0;
  color: #64736a;
  font-size: 14px;
  line-height: 1.5;
  text-align: center;
}

.trust-grid {
  article {
    text-align: center;
  }

  p {
    margin-top: 10px;
    color: #627068;
    font-size: 14px;
    line-height: 1.5;
  }
}

.help-section {
  display: grid;
  grid-template-columns: minmax(260px, 0.38fr) minmax(0, 0.62fr);
  gap: clamp(28px, 6vw, 82px);

  h2 {
    color: #07140f;
    font-size: clamp(32px, 4vw, 52px);
    font-weight: 900;
    line-height: 1.08;
  }

  p {
    margin-top: 15px;
    color: #5f6e66;
    font-size: 16px;
    line-height: 1.55;
  }
}

.help-list {
  display: grid;
  gap: 1px;
  background: #dfe8e2;
  border: 1px solid #dfe8e2;
  border-radius: 8px;
  overflow: hidden;

  article {
    padding: 24px;
    background: #ffffff;
  }

  strong {
    color: #07140f;
    font-size: 17px;
    font-weight: 900;
  }

  p {
    margin-top: 8px;
    color: #5f6e66;
    font-size: 15px;
  }
}

.final-cta {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #ffffff;
  background: #004630;
  text-align: center;

  h2 {
    max-width: 760px;
    font-size: clamp(34px, 4.4vw, 58px);
    font-weight: 900;
    line-height: 1.05;
  }

  p {
    max-width: 620px;
    margin-top: 14px;
    color: rgba(255, 255, 255, 0.78);
    font-size: 17px;
    line-height: 1.5;
  }
}

@media (max-width: 1160px) {
  .nav-actions {
    gap: 16px;
  }

  .help-section {
    grid-template-columns: 1fr;
  }

  .hero-section {
    min-height: auto;
  }

  .trust-grid,
  .landing-plans-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .landing-plans-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .landing-plan-card {
    min-height: 314px;
    padding: 24px 18px 18px;

    h3 {
      font-size: 21px;
    }

    p {
      min-height: 40px;
      margin-top: 8px;
      font-size: 12px;
      line-height: 1.3;
    }
  }

  .landing-plan-price {
    min-height: 50px;
    margin-top: 10px;

    small,
    em {
      font-size: 12px;
    }

    strong {
      font-size: 34px;
    }

    .custom-price {
      font-size: 20px;
    }
  }

  .plan-limit {
    font-size: 12px;
  }

  .plan-capacity {
    margin-top: 10px;
    gap: 7px;

    div {
      min-height: 48px;
      padding: 8px 9px;
    }

    span {
      font-size: 11px;
    }

    strong {
      margin-top: 3px;
      font-size: 15px;
    }
  }

  .landing-plan-card .plan-includes {
    margin-top: 12px;
    font-size: 12px;
  }

  .plan-action {
    margin-top: 14px;
    min-height: 38px;
    font-size: 13px;
  }

  .plan-ribbon {
    height: 28px;
    font-size: 11px;
  }

  .preview-section {
    grid-template-columns: minmax(280px, 0.44fr) minmax(0, 0.56fr);
    gap: 34px;
  }

  .preview-copy h2 {
    font-size: clamp(30px, 3.6vw, 44px);
  }

  .desktop-preview {
    margin-left: 54px;
  }

  .desktop-body {
    grid-template-columns: 112px 1fr;
    gap: 16px;
  }

  .summary-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 980px) {
  .preview-section {
    grid-template-columns: 1fr;
  }

  .landing-plans-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 880px) {
  .nav-actions a:not(.nav-button) {
    display: none;
  }

  .intro-strip,
  .resource-grid,
  .steps-list,
  .landing-plans-grid {
    grid-template-columns: 1fr;
  }

  .audience-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .intro-strip article,
  .landing-plan-card {
    border-right: 0;
    border-left: 0;
    border-top: 1px solid #dfe8e2;
  }

  .intro-strip article:first-child,
  .landing-plan-card:first-child {
    border-top: 0;
  }

  .landing-plan-card {
    min-height: auto;
    padding: 24px 18px 18px;

    p {
      min-height: auto;
    }
  }

  .landing-plan-price {
    min-height: 48px;

    strong {
      font-size: 34px;
    }
  }

  .plan-capacity {
    grid-template-columns: repeat(2, minmax(0, 1fr));

    div {
      min-height: 50px;
    }
  }

  .steps-list article:not(:last-child)::after {
    display: none;
  }

  .product-preview {
    min-height: auto;
  }

  .phone-preview {
    position: relative;
    left: auto;
    bottom: auto;
    width: auto;
    max-width: 320px;
    margin: 0 auto 18px;
  }

  .desktop-preview {
    margin-left: 0;
  }
}

@media (max-width: 640px) {
  .landing-nav,
  .hero-copy,
  .section-block,
  .steps-section,
  .preview-section,
  .audience-section,
  .pricing-section,
  .trust-section,
  .help-section,
  .final-cta {
    padding-inline: 16px;
  }

  .brand strong {
    font-size: 21px;
  }

  .nav-button {
    min-height: 38px;
    padding: 0 13px;
  }

  .hero-copy {
    width: auto;
    padding-top: 46px;
    padding-bottom: 46px;
  }

  .hero-audience {
    > div {
      grid-template-columns: 1fr;
      max-width: 330px;
    }

    strong {
      justify-content: flex-start;
      padding: 0 14px;
      font-size: 13px;
    }
  }

  .hero-section {
    display: block;
    background-image: none;
  }

  .hero-image {
    display: block;
    min-height: 300px;
    background: #eef5ef;

    img {
      min-height: 300px;
    }
  }

  .resource-grid,
  .audience-grid,
  .trust-grid,
  .desktop-body,
  .summary-grid {
    grid-template-columns: 1fr;
  }

  .resource-grid article,
  .audience-grid article,
  .trust-grid article {
    min-height: auto;
  }

  .desktop-body aside {
    display: none;
  }

  .landing-plan-card {
    min-height: auto;
    padding: 22px 16px 16px;
  }

  .hero-actions {
    width: 100%;
  }

  .primary-action,
  .secondary-action {
    flex: 1 1 150px;
  }
}
</style>
